import { Role } from './role.entity';
import type { ProfileStateType } from '../value_objects/profile-state';

/**
 * User Entity
 * Represents a user in the system with their roles and permissions
 */
export class User {
    private constructor(
        private readonly _userId: string,
        private readonly _email: string,
        private readonly _firstName: string,
        private readonly _middleName: string | null,
        private readonly _lastName: string,
        private readonly _displayName: string,
        private readonly _fullName: string,
        private readonly _profilePictureUrl: string | null,
        private readonly _phone: string,
        private readonly _isActive: boolean,
        private readonly _isVerified: boolean,
        private readonly _isBanned: boolean,
        private readonly _createdAt: Date,
        private readonly _updatedAt: Date,
        private readonly _universityId: string | null,
        private readonly _roles: Role[],
        private readonly _permissions: string[],
        private readonly _profileState: ProfileStateType,
        private readonly _whatsappLinked: boolean
    ) { }

    static create(props: {
        userId: string;
        email: string;
        firstName: string;
        middleName: string | null;
        lastName: string;
        displayName: string;
        fullName: string;
        profilePictureUrl: string | null;
        phone: string;
        isActive: boolean;
        isVerified: boolean;
        isBanned: boolean;
        createdAt: Date;
        updatedAt: Date;
        universityId: string | null;
        roles: Role[];
        permissions: string[];
        profileState?: ProfileStateType;
        whatsappLinked?: boolean;
    }): User {
        return new User(
            props.userId,
            props.email,
            props.firstName,
            props.middleName,
            props.lastName,
            props.displayName,
            props.fullName,
            props.profilePictureUrl,
            props.phone,
            props.isActive,
            props.isVerified,
            props.isBanned,
            props.createdAt,
            props.updatedAt,
            props.universityId,
            props.roles,
            props.permissions,
            props.profileState || 'ACTIVE',
            props.whatsappLinked || false
        );
    }

    // Getters
    get userId(): string {
        return this._userId;
    }

    get email(): string {
        return this._email;
    }

    get firstName(): string {
        return this._firstName;
    }

    get middleName(): string | null {
        return this._middleName;
    }

    get lastName(): string {
        return this._lastName;
    }

    get displayName(): string {
        return this._displayName;
    }

    get fullName(): string {
        return this._fullName;
    }

    get profilePictureUrl(): string | null {
        return this._profilePictureUrl;
    }

    get phone(): string {
        return this._phone;
    }

    get isActive(): boolean {
        return this._isActive;
    }

    get isVerified(): boolean {
        return this._isVerified;
    }

    get isBanned(): boolean {
        return this._isBanned;
    }

    get createdAt(): Date {
        return this._createdAt;
    }

    get updatedAt(): Date {
        return this._updatedAt;
    }

    get universityId(): string | null {
        return this._universityId;
    }

    get roles(): Role[] {
        return [...this._roles];
    }

    get permissions(): string[] {
        return [...this._permissions];
    }

    get profileState(): ProfileStateType {
        return this._profileState;
    }

    get whatsappLinked(): boolean {
        return this._whatsappLinked;
    }

    /**
     * Check if user's profile is complete and active
     */
    isProfileComplete(): boolean {
        return this._profileState === 'ACTIVE';
    }

    /**
     * Check if user has a specific permission
     */
    hasPermission(permission: string): boolean {
        // Check direct permissions
        if (this._permissions.includes('*') || this._permissions.includes(permission)) {
            return true;
        }

        // Check role permissions
        return this._roles.some(role => role.hasPermission(permission));
    }

    /**
     * Check if user has all specified permissions
     */
    hasAllPermissions(permissions: string[]): boolean {
        return permissions.every(permission => this.hasPermission(permission));
    }

    /**
     * Check if user has any of the specified permissions
     */
    hasAnyPermission(permissions: string[]): boolean {
        return permissions.some(permission => this.hasPermission(permission));
    }

    /**
     * Check if user has a specific role
     */
    hasRole(roleName: string): boolean {
        return this._roles.some(role => role.name === roleName);
    }

    /**
     * Check if user can access the system
     */
    canAccess(): boolean {
        return this._isActive && this._isVerified && !this._isBanned;
    }

    /**
     * Get all role names
     */
    getRoleNames(): string[] {
        return this._roles.map(role => role.name);
    }

    /**
     * Check if user is super admin
     */
    isSuperAdmin(): boolean {
        return this.hasRole('super_admin') || this.hasPermission('*');
    }
}
